import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ToolHistoryContext = createContext();

export const useToolHistory = () => {
  const context = useContext(ToolHistoryContext);
  if (!context) {
    throw new Error('useToolHistory must be used within a ToolHistoryProvider');
  }
  return context;
};

// Tool definitions with icons and labels
export const TOOL_DEFINITIONS = {
  timemanagement: { id: 'timemanagement', label: 'Time Management', icon: '⏱️', color: 'blue' },
  priority: { id: 'priority', label: 'Priority Matrix', icon: '📊', color: 'amber' },
  milestones: { id: 'milestones', label: 'Milestone Tracker', icon: '🎯', color: 'indigo' },
  finance: { id: 'finance', label: 'Financial Tracker', icon: '💰', color: 'teal' },
  buildtracker: { id: 'buildtracker', label: 'Build Tracker', icon: '🔨', color: 'orange' },
  notes: { id: 'notes', label: 'Notes', icon: '📝', color: 'pink' },
  'task-breakdown': { id: 'task-breakdown', label: 'Task Breakdown', icon: '⚡', color: 'purple' },
  teamhub: { id: 'teamhub', label: 'Team Space', icon: '👥', color: 'blue' }
};

export const ToolHistoryProvider = ({ children }) => {
  const [toolHistory, setToolHistory] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('toolHistory');
    return saved ? JSON.parse(saved) : [];
  });

  const [activeTool, setActiveTool] = useState(null);

  // Save to localStorage whenever history changes
  useEffect(() => {
    localStorage.setItem('toolHistory', JSON.stringify(toolHistory));
  }, [toolHistory]);

  // Add a tool to history (move to top if already exists)
  const addToHistory = useCallback((toolId) => {
    if (!TOOL_DEFINITIONS[toolId]) return;
    
    setToolHistory(prev => {
      // Remove if already in history
      const filtered = prev.filter(id => id !== toolId);
      // Add to beginning, keep max 5 items
      return [toolId, ...filtered].slice(0, 5);
    });
    setActiveTool(toolId);
  }, []);

  // Remove a tool from history
  const removeFromHistory = useCallback((toolId) => {
    setToolHistory(prev => prev.filter(id => id !== toolId));
    setActiveTool(prev => prev === toolId ? null : prev);
  }, []);

  // Clear all history
  const clearHistory = useCallback(() => {
    setToolHistory([]);
    setActiveTool(null);
  }, []);

  // Get tool info by ID
  const getToolInfo = useCallback((toolId) => TOOL_DEFINITIONS[toolId] || null, []);

  const value = useMemo(() => ({
    toolHistory,
    activeTool,
    setActiveTool,
    addToHistory,
    removeFromHistory,
    clearHistory,
    getToolInfo,
    TOOL_DEFINITIONS
  }), [toolHistory, activeTool, addToHistory, removeFromHistory, clearHistory, getToolInfo]);

  return (
    <ToolHistoryContext.Provider value={value}>
      {children}
    </ToolHistoryContext.Provider>
  );
};

export default ToolHistoryContext;
